import React, { useState, useRef, useEffect } from 'react'
import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Switch } from '../Input'
import Select from 'react-select'

export interface FilterSection {
  id: string
  title: string
  filters: FilterItem[]
}

export interface FilterItem {
  id: string
  label: string
  type: 'switch' | 'select' | 'date-range'
  checked?: boolean
  onToggle?: (checked: boolean) => void
  selectProps?: {
    isMulti?: boolean
    options: Array<{ label: string; value: string }>
    value: any
    onChange: (value: any) => void
    placeholder?: string
  }
  dateRangeProps?: {
    start: string
    end: string
    onStartChange: (value: string) => void
    onEndChange: (value: string) => void
  }
}

export interface FilterPanelProps {
  isOpen: boolean
  onClose: () => void
  sections: FilterSection[]
  onClearAll: () => void
  activeFiltersCount: number
  position?: 'left' | 'right'
  className?: string
  searchInput?: {
    value: string
    onChange: (value: string) => void
    placeholder?: string
  }
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  isOpen,
  onClose,
  sections,
  onClearAll,
  activeFiltersCount,
  position = 'right',
  className = '',
  searchInput
}) => {
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
  const panelRef = useRef<HTMLDivElement>(null)

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Prevenir scroll del body cuando está abierto en móvil
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => {
      const newSet = new Set(prev)
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId)
      } else {
        newSet.add(sectionId)
      }
      return newSet
    })
  }

  const isSectionCollapsed = (sectionId: string) => collapsedSections.has(sectionId)

  return (
    <>
      {/* Overlay para móvil */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden transition-opacity duration-300"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      {isOpen && (
        <div
          ref={panelRef}
          className={`
            fixed md:absolute
            top-0 md:top-auto
            bottom-0 md:bottom-auto
            w-full md:w-96
            h-full md:h-auto md:max-h-[80vh]
            md:mt-2
            bg-white
            rounded-lg md:rounded-lg
            shadow-xl
            z-[100]
            flex flex-col
            ${position === 'right' ? 'right-0' : 'left-0'}
            ${className}
          `}
        >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <h3 className="text-lg">Filtros</h3> 
            {activeFiltersCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                {activeFiltersCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFiltersCount > 0 && (
              <button
                onClick={onClearAll}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Limpiar todo
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              aria-label="Cerrar filtros"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {sections.map((section) => (
              <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <span className="text-sm font-semibold text-gray-700">{section.title}</span>
                  <ChevronDownIcon
                    className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                      isSectionCollapsed(section.id) ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Section Content */}
                {!isSectionCollapsed(section.id) && (
                  <div className="p-3 space-y-4 animate-fade-in">
                    {section.filters.map((filter) => (
                      <div key={filter.id}>
                        {filter.type === 'switch' && (
                          <div className="flex items-center justify-between">
                            <Switch
                              checked={filter.checked || false}
                              onChange={filter.onToggle || (() => {})}
                              label={filter.label}
                            />
                          </div>
                        )}

                        {filter.type === 'switch' && filter.checked && filter.selectProps && (
                          <div className="mt-3">
                            <Select
                              {...filter.selectProps}
                              className="react-select-container"
                              classNamePrefix="react-select"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                              }}
                            />
                          </div>
                        )}

                        {filter.type === 'select' && filter.selectProps && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              {filter.label}
                            </label>
                            <Select
                              {...filter.selectProps}
                              className="react-select-container"
                              classNamePrefix="react-select"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{
                                menuPortal: (base) => ({ ...base, zIndex: 9999 })
                              }}
                            />
                          </div>
                        )}

                        {filter.type === 'switch' && filter.checked && filter.dateRangeProps && (
                          <div className="mt-3 grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Fecha inicio
                              </label>
                              <input
                                type="date"
                                value={filter.dateRangeProps.start}
                                onChange={(e) => filter.dateRangeProps?.onStartChange(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Fecha fin
                              </label>
                              <input
                                type="date"
                                value={filter.dateRangeProps.end}
                                onChange={(e) => filter.dateRangeProps?.onEndChange(e.target.value)}
                                className="w-full p-2 border border-gray-300 rounded-md text-sm"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        </div>
      )}
    </>
  )
}

export default FilterPanel

